import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Availability } from '@/lib/availability';

/*
 * Users API — read-only post-Phase-15.
 *
 * Two remaining surfaces:
 *
 * 1. Staff directory — GET /api/users/directory — returns lightweight
 *    {id, first, last, npi, taxonomy, roleName, roleType} for Active
 *    *staff* users. Used by dropdowns everywhere (assign BCBA, session
 *    clinician, contact-log author). Parent/Guardian accounts are
 *    excluded server-side.
 *
 * 2. Self profile + email prefs — GET /api/users/me*, PATCH /api/users/me/email-prefs.
 *
 * The /api/users?view=certs cert-rollup endpoint was retired
 * 2026-05-19 along with the credentialExpiry job; cert expiration
 * tracking lives in admin-server's compliance domain per ADR 0012.
 *
 * Mutations (POST/PATCH/DELETE /api/users) were retired in Phase 15 along
 * with the Staff / Roles / Parent-Accounts UI; user management lives in
 * the Home app and goes through admin-server.
 */

export type RoleType = 'BCBA' | 'RBT' | 'none' | 'parent';

// 'Invited' is the post-create state Cognito uses while an invite email is
// outstanding. PMA reads it but doesn't issue it anymore.
export type UserStatus = 'Active' | 'Inactive' | 'Invited';

// ── Staff directory (lightweight, dropdowns) ─────────────────────

export interface DirectoryEntry {
    id: string;
    first: string;
    last: string;
    /** Rendering-provider NPI. Empty string when unset. */
    npi: string;
    /** Rendering-provider taxonomy (e.g. 103K00000X). Empty string when unset. */
    taxonomy: string;
    roleName: string;
    roleType: RoleType;
    /**
     * Weekly availability pattern used by the scheduler to warn when a
     * session falls outside the staff or supervising-BCBA window.
     */
    availability: Availability;
}

async function fetchDirectory(roleType: RoleType | null): Promise<DirectoryEntry[]> {
    const res = await api.get<DirectoryEntry[]>('/users/directory', {
        params: roleType ? { roleType } : undefined,
    });
    return res.data;
}

export function useUserDirectory(roleType: RoleType | null = null) {
    return useQuery({
        queryKey: ['users', 'directory', roleType] as const,
        queryFn: () => fetchDirectory(roleType),
        staleTime: 30_000,
        refetchOnMount: 'always',
    });
}

// ── My profile + email prefs ─────────────────────────────────────

export interface MyProfile {
    id: string;
    first: string;
    last: string;
    email: string;
    roleId: string | null;
    roleName: string;
    roleType: RoleType | '';
    status: UserStatus;
    bacbCertNumber: string;
    bacbCertName: string;
    secondaryCertName: string;
    secondaryCertNumber: string;
}

async function fetchMyProfile(): Promise<MyProfile> {
    const res = await api.get<MyProfile>('/users/me');
    return res.data;
}

export function useMyProfile() {
    return useQuery({
        queryKey: ['users', 'me'] as const,
        queryFn: fetchMyProfile,
        staleTime: 5 * 60_000,
    });
}

export interface MyEmailPrefs {
    portalMessage?: boolean;
    signatureRequest?: boolean;
    sessionReminder?: boolean;
    scheduleChange?: boolean;
    missingNote?: boolean;
    authExpiring?: boolean;
    claimDenied?: boolean;
}

async function fetchMyEmailPrefs(): Promise<MyEmailPrefs> {
    const res = await api.get<MyEmailPrefs>('/users/me/email-prefs');
    return res.data;
}

async function updateMyEmailPrefs(payload: MyEmailPrefs): Promise<MyEmailPrefs> {
    const res = await api.patch<MyEmailPrefs>('/users/me/email-prefs', payload);
    return res.data;
}

export function useMyEmailPrefs() {
    return useQuery({
        queryKey: ['users', 'me', 'email-prefs'] as const,
        queryFn: fetchMyEmailPrefs,
        staleTime: 60_000,
    });
}

export function useUpdateMyEmailPrefs() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: updateMyEmailPrefs,
        onSuccess: (data) => {
            qc.setQueryData(['users', 'me', 'email-prefs'], data);
        },
    });
}
